import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { PaymentInput } from "@/components/PaymentInput";
import { useApi, errMsg } from "@/lib/api";

const FIELDS = [
  ["name", "Ragione sociale"], ["address", "Indirizzo"], ["city", "CAP e città"],
  ["piva", "P.IVA"], ["cf", "Codice fiscale"], ["email", "Email"], ["pec", "PEC"], ["sdi", "Codice SDI"], ["phone", "Telefono"],
];

const ClientDialog = ({ client, options, onClose, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState({});
  useEffect(() => { if (client) setF({ ...client }); }, [client]);
  const save = async () => {
    if (!f.name?.trim()) return toast.error("Inserisci la ragione sociale");
    const body = { ...Object.fromEntries(FIELDS.map(([k]) => [k, f[k] || ""])), sconto_materiale: +f.sconto_materiale || 0, sconto_totale: +f.sconto_totale || 0, payment_terms: f.payment_terms || "" };
    try {
      if (client.id) await api.put(`/clients/${client.id}`, body); else await api.post("/clients", body);
      toast.success("Cliente salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 max-h-[92vh] overflow-y-auto" data-testid="client-dialog">
        <DialogHeader><DialogTitle>{client?.id ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, label]) => (
            <div key={k} className={`space-y-1.5 ${k === "name" ? "sm:col-span-2" : ""}`}>
              <Label className="micro-label">{label}</Label>
              <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`client-${k}-input`} />
            </div>
          ))}
          <div className="sm:col-span-2 border-t border-slate-800 pt-3 mt-1 micro-label text-amber-300">Condizioni commerciali</div>
          <div className="space-y-1.5"><Label className="micro-label">1° sconto su materiale %</Label><Input type="number" min={0} max={100} step="any" value={f.sconto_materiale ?? ""} onChange={(e) => setF({ ...f, sconto_materiale: e.target.value })} className="font-mono" data-testid="client-sconto-materiale-input" /></div>
          <div className="space-y-1.5"><Label className="micro-label">2° sconto sul totale %</Label><Input type="number" min={0} max={100} step="any" value={f.sconto_totale ?? ""} onChange={(e) => setF({ ...f, sconto_totale: e.target.value })} className="font-mono" data-testid="client-sconto-totale-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="micro-label">Modalità di saldo</Label><PaymentInput value={f.payment_terms} onChange={(v) => setF({ ...f, payment_terms: v })} options={options} id="client-payment-options" testId="client-payment-input" /></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button><Button onClick={save} data-testid="client-save-btn">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ClientsPage() {
  const api = useApi();
  const [clients, setClients] = useState([]);
  const [edit, setEdit] = useState(null);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setOptions(r.data.payment_options || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (c) => { try { await api.delete(`/clients/${c.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const shown = clients.filter((c) => [c.name, c.piva, c.city].join(" ").toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-6 rise" data-testid="clients-page">
      <div className="flex flex-wrap items-end gap-4">
        <div className="mr-auto"><p className="micro-label">Anagrafica</p><h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Clienti</h1></div>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca cliente" className="w-full sm:w-64" data-testid="clients-search-input" />
        <Button onClick={() => setEdit({})} data-testid="client-new-btn"><Plus className="h-4 w-4 mr-1" />Nuovo cliente</Button>
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto">
        <table className="w-full text-left" data-testid="clients-table">
          <thead><tr className="micro-label border-b border-slate-800"><th className="py-2 px-3">Cliente</th><th className="py-2 px-3 hidden md:table-cell">Indirizzo</th><th className="py-2 px-3 hidden sm:table-cell">P.IVA</th><th className="py-2 px-3 hidden lg:table-cell whitespace-nowrap">Sconti / Pagamento</th><th /></tr></thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.id} className="border-b border-slate-800/70 hover:bg-slate-800/30" data-testid={`client-row-${c.id}`}>
                <td className="py-3 px-3"><div className="font-medium">{c.name}</div><div className="text-xs text-slate-500">{[c.email, c.phone].filter(Boolean).join(" · ")}</div></td>
                <td className="py-3 px-3 text-sm text-slate-400 hidden md:table-cell">{[c.address, c.city].filter(Boolean).join(", ")}</td>
                <td className="py-3 px-3 font-mono text-sm hidden sm:table-cell">{c.piva}</td>
                <td className="py-3 px-3 text-xs hidden lg:table-cell" data-testid={`client-terms-${c.id}`}>
                  {(c.sconto_materiale > 0 || c.sconto_totale > 0) && <div className="font-mono text-amber-300">Mat. {(+c.sconto_materiale || 0).toLocaleString("it-IT")}% · Tot. {(+c.sconto_totale || 0).toLocaleString("it-IT")}%</div>}
                  <div className="text-slate-400 line-clamp-1">{c.payment_terms}</div>
                </td>
                <td className="py-3 px-3"><div className="flex justify-end gap-1">
                  <Button size="icon" variant="ghost" onClick={() => setEdit(c)} data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <ConfirmDelete onConfirm={() => del(c)} title={`Eliminare ${c.name}?`} testId={`client-del-${c.id}`}><Trash2 className="h-4 w-4" /></ConfirmDelete>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && <p className="p-8 text-center text-sm text-slate-400">Nessun cliente.</p>}
      </div>
      <ClientDialog client={edit} options={options} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
